/**
 * Integrytas Group - Common Security Module (CSM)
 * TabStrip — the Multi-Tab Manager strip (Direction D, design/desktop).
 *
 * Discrete tabs, not a text strip: inactive tabs sit at 32.5px with their own
 * fill (#E4E9EF) and hairline; the active tab is 36.5px, white and blue-
 * outlined, pulled up so it visually connects to the breadcrumb bar beneath.
 * Each tab carries its module icon and a close control — or an AMBER DOT in
 * place of the close control when the tab holds unsaved changes (the dot is
 * driven by the `isDirty` predicate; the real per-tab dirty tracking + close
 * confirmation are wired by the shell, see Step 3). The strip ends with an
 * overflow chip ("N more"), a split-screen toggle and close-all.
 *
 * The tab-strip colours are intentionally literal design hexes (matching the
 * rest of the shell chrome) rather than theme tokens; a full dark-mode pass of
 * the shell chrome is tracked separately, as noted in TabManagerShell.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronDown, Columns2, RefreshCw } from 'lucide-react';
import type { OpenTab } from './TabManagerShell';
import { moduleIconFor } from './module_icons';

export interface TabStripProps {
  tabs: OpenTab[];
  activeCode: string | null;
  pageLabels: Record<string, string>;
  isSplit: boolean;
  paneLeftCode: string | null;
  paneRightCode: string | null;
  onFocus: (code: string) => void;
  onCloseTab: (code: string) => void;
  onCloseAll: () => void;
  onToggleSplit: () => void;
  /** Reload the active page. Present only when the active tab registered a
   *  reloader (usePageRefresh); the strip shows the Refresh control accordingly. */
  onRefresh?: () => void;
  /** True when the tab holds unsaved changes — shows the amber dot in place of
   *  its close control. Defaults to never-dirty; the shell supplies the real
   *  predicate (Step 3). */
  isDirty?: (code: string) => boolean;
}

const GAP = 3;               // px between tabs (matches the design strip gap)
const TOOLS_RESERVE = 100;   // px kept clear for the "N more" overflow chip

/** Estimated tab width for a tab that has not been measured yet. */
function estimateWidth(label: string): number {
  return Math.min(240, 62 + label.length * 6.7);
}

export function TabStrip({
  tabs, activeCode, pageLabels, isSplit, paneLeftCode, paneRightCode,
  onFocus, onCloseTab, onCloseAll, onToggleSplit, onRefresh, isDirty,
}: TabStripProps) {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLDivElement>(null);
  const widthCache = useRef<Map<string, number>>(new Map());
  const [rowWidth, setRowWidth] = useState(0);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  const labelFor = useCallback(
    (tab: OpenTab) => pageLabels[tab.page_internal_code] ?? tab.display_text,
    [pageLabels],
  );

  // Track the measurable width of the tab row.
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) setRowWidth(e.contentRect.width);
    });
    ro.observe(el);
    setRowWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Close the overflow dropdown on outside-click / Esc.
  useEffect(() => {
    if (!overflowOpen) return;
    const onClick = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) setOverflowOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOverflowOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [overflowOpen]);

  // Cache each rendered tab's real width so overflow maths is accurate.
  const measureTab = useCallback((code: string) => (el: HTMLDivElement | null) => {
    if (el) widthCache.current.set(code, el.getBoundingClientRect().width);
  }, []);

  // Split the open tabs into a visible prefix and an overflow remainder. The
  // active tab is always kept visible.
  const { visibleTabs, overflowTabs } = useMemo(() => {
    if (rowWidth <= 0 || tabs.length === 0) {
      return { visibleTabs: tabs, overflowTabs: [] as OpenTab[] };
    }
    const widthOf = (tab: OpenTab) =>
      widthCache.current.get(tab.page_internal_code) ?? estimateWidth(labelFor(tab));

    const totalAll = tabs.reduce((sum, tab) => sum + widthOf(tab) + GAP, 0);
    if (totalAll <= rowWidth) return { visibleTabs: tabs, overflowTabs: [] };

    const avail = rowWidth - TOOLS_RESERVE;
    const visible: OpenTab[] = [];
    let used = 0;
    for (const tab of tabs) {
      const w = widthOf(tab) + GAP;
      if (used + w <= avail) { visible.push(tab); used += w; } else break;
    }
    // Guarantee the active tab is visible.
    const active = tabs.find(tab => tab.page_internal_code === activeCode);
    if (active && !visible.some(tab => tab.page_internal_code === activeCode)) {
      const aw = widthOf(active) + GAP;
      while (visible.length > 0 && used + aw > avail) {
        used -= widthOf(visible.pop() as OpenTab) + GAP;
      }
      visible.push(active);
    }
    const visibleCodes = new Set(visible.map(tab => tab.page_internal_code));
    const overflow = tabs.filter(tab => !visibleCodes.has(tab.page_internal_code));
    return { visibleTabs: visible, overflowTabs: overflow };
  }, [tabs, rowWidth, activeCode, labelFor]);

  const hasTabs = tabs.length > 0;
  const canSplit = tabs.length >= 2;   // split needs a tab to pin AND at least one other

  return (
    <div
      className="flex h-[42px] shrink-0 items-end gap-[3px] overflow-hidden border-b border-csm-line px-3"
      style={{ backgroundColor: '#F8FAFC', marginBottom: '-0.5px' }}
    >
      {/* Tab row — flex-1 so the strip tools can pin to the right. */}
      <div ref={rowRef} className="flex min-w-0 flex-1 items-end gap-[3px]">
        {!hasTabs && (
          <div className="self-center px-1 text-csm-sm italic text-csm-muted">
            {t('shell.no_pages_open')}
          </div>
        )}
        {visibleTabs.map(tab => (
          <TabButton
            key={tab.page_internal_code}
            measureRef={measureTab(tab.page_internal_code)}
            tab={tab}
            label={labelFor(tab)}
            isActive={tab.page_internal_code === activeCode}
            inPane={isSplit && (tab.page_internal_code === paneLeftCode || tab.page_internal_code === paneRightCode)}
            dirty={isDirty?.(tab.page_internal_code) ?? false}
            onFocus={onFocus}
            onClose={onCloseTab}
          />
        ))}
      </div>

      {/* Strip tools — always present so the split toggle is always visible.
          Overflow and close-all appear only when there are tabs. */}
      <div className="flex shrink-0 items-center gap-[4px] pb-[5px]">
          {/* Overflow chip — lists tabs that didn't fit. */}
          {overflowTabs.length > 0 && (
            <div ref={overflowRef} className="relative">
              <button
                type="button"
                onClick={() => setOverflowOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={overflowOpen}
                className="flex h-[26px] items-center gap-[5px] rounded-[5px] border border-csm-line bg-white px-2 text-csm-xs font-semibold text-csm-ink-soft hover:border-primary hover:text-primary"
                title={t('shell.more_tabs_title', 'More open tabs')}
              >
                <span>{t('shell.n_more', '{{count}} more', { count: overflowTabs.length })}</span>
                <ChevronDown className="h-3 w-3 text-csm-muted" />
              </button>
              {overflowOpen && (
                <div role="menu" className="absolute right-0 top-[30px] z-[60] min-w-[13rem] max-w-[20rem] rounded-md border border-csm-border bg-white py-1 elev-1 anim-pop">
                  {overflowTabs.map(tab => {
                    const Icon = moduleIconFor(tab.page_internal_code, labelFor(tab));
                    const dirty = isDirty?.(tab.page_internal_code) ?? false;
                    return (
                      <div key={tab.page_internal_code} className="group flex items-center hover:bg-csm-surface-alt">
                        <button
                          role="menuitem"
                          type="button"
                          onClick={() => { onFocus(tab.page_internal_code); setOverflowOpen(false); }}
                          className="flex min-w-0 flex-1 items-center gap-2 px-3 py-1.5 text-left text-csm-sm text-csm-ink"
                        >
                          <Icon className="h-[14px] w-[14px] shrink-0 text-csm-muted" />
                          <span className="flex-1 truncate">{labelFor(tab)}</span>
                          {dirty && <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-csm-warning" aria-hidden />}
                        </button>
                        <button
                          type="button"
                          onClick={() => onCloseTab(tab.page_internal_code)}
                          aria-label={t('shell.close_tab_label', 'Close {{title}}', { title: labelFor(tab) })}
                          className="mr-2 flex h-5 w-5 items-center justify-center rounded text-csm-muted hover:bg-csm-border hover:text-csm-ink"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Split-screen toggle — shown only when there are at least two tabs
              (one to pin on the left, one for the right workspace). */}
          {canSplit && (
            <button
              type="button"
              onClick={onToggleSplit}
              aria-pressed={isSplit}
              title={isSplit ? t('shell.unsplit_title', 'Return to a single pane') : t('shell.split_view_title', 'Split view (side-by-side)')}
              className={
                'flex h-[26px] w-[26px] items-center justify-center rounded-[5px] border ' +
                (isSplit ? 'border-primary bg-primary-soft text-primary' : 'border-csm-line bg-white text-csm-ink-soft hover:border-primary hover:text-primary')
              }
            >
              <Columns2 className="h-[13px] w-[13px]" />
            </button>
          )}

          {/* Refresh the active page — between split and close-all. Shown only
              when the active page registered a reloader (usePageRefresh). */}
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              title={t('shell.refresh_title', 'Refresh this page')}
              aria-label={t('shell.refresh_title', 'Refresh this page')}
              className="flex h-[26px] w-[26px] items-center justify-center rounded-[5px] border border-csm-line bg-white text-csm-ink-soft hover:border-primary hover:text-primary"
            >
              <RefreshCw className="h-[13px] w-[13px]" />
            </button>
          )}

          {/* Close all */}
          {hasTabs && (
            <button
              type="button"
              onClick={onCloseAll}
              title={t('shell.close_all_title', 'Close all tabs')}
              aria-label={t('shell.close_all_title', 'Close all tabs')}
              className="flex h-[26px] w-[26px] items-center justify-center rounded-[5px] border border-csm-line bg-white text-csm-ink-soft hover:border-csm-danger hover:text-csm-danger"
            >
              <X className="h-[13px] w-[13px]" />
            </button>
          )}
        </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A single tab.
// ---------------------------------------------------------------------------

interface TabButtonProps {
  measureRef?: (el: HTMLDivElement | null) => void;
  tab: OpenTab;
  label: string;
  isActive: boolean;
  inPane: boolean;
  dirty: boolean;
  onFocus: (code: string) => void;
  onClose: (code: string) => void;
}

function TabButton({ measureRef, tab, label, isActive, inPane, dirty, onFocus, onClose }: TabButtonProps) {
  const { t } = useTranslation();
  const Icon = moduleIconFor(tab.page_internal_code, label);

  // Exact Direction-D geometry (design/desktop): active 36.5px white with a
  // 3px blue top border pulled up over the strip's baseline; inactive 32.5px
  // on the #E4E9EF fill with a 1px hairline.
  const shellStyle: React.CSSProperties = isActive
    ? {
        height: '36.5px', backgroundColor: '#FFFFFF',
        borderStyle: 'solid', borderColor: '#00529B',
        borderWidth: '3px 1px 0 1px', borderRadius: '7px 7px 0 0',
        margin: '-1.5px -0.5px 0 -0.5px',
      }
    : {
        height: '32.5px', backgroundColor: inPane ? '#D6E4F2' : '#E4E9EF',
        borderStyle: 'solid', borderColor: '#D3DAE3',
        borderWidth: '1px 1px 0 1px', borderRadius: '7px 7px 0 0',
        margin: '-0.5px -0.5px 0 -0.5px',
      };

  return (
    <div
      ref={measureRef}
      onClick={() => onFocus(tab.page_internal_code)}
      className="group flex max-w-[240px] shrink-0 cursor-pointer select-none items-center gap-2 px-[13px]"
      style={shellStyle}
    >
      <Icon
        className="h-[14px] w-[14px] shrink-0"
        style={{ color: isActive ? '#00529B' : '#9AA4B2' }}
        aria-hidden
      />
      <span
        className={'truncate text-[12.5px] ' + (isActive ? 'font-semibold text-[#1F2937]' : 'font-medium text-[#7A8595]')}
      >
        {label}
      </span>

      {dirty ? (
        // Unsaved-changes indicator — replaces the close control (Step 3 wires
        // the real dirty state and the close-confirmation prompt).
        <span
          className="h-[7px] w-[7px] shrink-0 rounded-full"
          style={{ backgroundColor: '#9A5B00' }}
          title={t('shell.unsaved_title', 'Unsaved changes')}
          aria-label={t('shell.unsaved_title', 'Unsaved changes')}
        />
      ) : isActive ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(tab.page_internal_code); }}
          aria-label={t('shell.close_tab_label', 'Close {{title}}', { title: label })}
          className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full hover:brightness-95"
          style={{ backgroundColor: '#EDF1F6' }}
        >
          <X className="h-[11px] w-[11px]" style={{ color: '#55606E' }} />
        </button>
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(tab.page_internal_code); }}
          aria-label={t('shell.close_tab_label', 'Close {{title}}', { title: label })}
          className="flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[4px] text-[#9AA4B2] hover:bg-black/5 hover:text-[#55606E]"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
